import type {
	AxiosInstance,
	AxiosRequestConfig,
	AxiosResponse,
	InternalAxiosRequestConfig,
} from 'axios';
import axios from 'axios';
import type {GetToken200} from "../api/generated/insights.schemas";


let myYoastAccessTokenFactory: () => string = () => {
	// Try the MyYoast access token cookie (if hosted on a .yoast.com domain)
	if( document?.cookie ){
		const match = document.cookie.match(new RegExp('(^| )access_token=([^;]+)'));
		if (match) {
			return decodeURIComponent(match[2]);
		}
	}
	// Fallback to VITE_MYYOAST_ACCESS_TOKEN environment variable (for local dev)
	return import.meta.env?.VITE_MYYOAST_ACCESS_TOKEN || '';
};

/**
 * Lightweight cookie reader (no external dependency) used for CSRF token.
 */
function getCookie(name: string): string | undefined {
	if (typeof document === 'undefined') return undefined;
	const value = `; ${document.cookie}`;
	const parts = value.split(`; ${name}=`);
	if (parts.length === 2) return parts.pop()?.split(';').shift();
}

/**
 * A place to retrieve / refresh an auth token (e.g. from localStorage, context, etc.)
 * Keep this synchronous – if you need async refresh logic, implement it in the
 * response interceptor (401 handling) to avoid blocking every request.
 */
const LOCAL_STORAGE_AUTH_KEY = 'yst-insights-token';

/**
 * Shared Axios instance used by Orval generated operations.
 * withCredentials ensures XSRF cookie flows; dynamic headers set in interceptors.
 */
const instance: AxiosInstance = axios.create({
	withCredentials: true,
});

// REQUEST INTERCEPTOR ----------------------------------------------
instance.interceptors.request.use(
	async (config: InternalAxiosRequestConfig) => {
		// CSRF token (Laravel style XSRF-TOKEN cookie) → X-XSRF-TOKEN header.
		const authToken = await login();
		const xsrfToken = await getXSRFToken();

		// Merge headers carefully preserving existing typing
		config.headers = {
			...(config.headers || {}),
			...(xsrfToken ? {'X-XSRF-TOKEN': xsrfToken} : {}),
			...(authToken ? {Authorization: `Bearer ${authToken}`} : {}),
		} as typeof config.headers;

		return config;
	},
	(error) => Promise.reject(error),
	{synchronous: false}
);

// RESPONSE INTERCEPTOR (optional skeleton for 401 refresh) ----------
// Customize if you need silent token refresh / logout redirects.
instance.interceptors.response.use(
	(response) => response,
	async (error) => {
		// Example 401 hook – add custom logic here (refresh token, redirect, etc.)
		if (error?.response?.status === 401) {
			// e.g., dispatch logout event or attempt refresh
			// window.dispatchEvent(new Event('auth:unauthorized'));
		}
		return Promise.reject(error);
	},
);

/**
 * customInstance
 *
 * This generic wrapper preserves full Axios typing while allowing Orval to
 * forward the expected <TData = any, TError = any> generics implicitly.
 *
 * Orval's generated functions pass only an AxiosRequestConfig. We maintain
 * type forwarding by returning a Promise<AxiosResponse<T, D>> where:
 *  - T is the inferred expected response data type (from the generated fn)
 *  - D is the request body type (default any) useful for mutation payloads.
 *
 * You can extend this further (circuit-breakers, retry, logging) without
 * touching generated code.
 */
export async function customInstance<
	T = unknown,
	D = unknown,
	C extends AxiosRequestConfig<D> = AxiosRequestConfig<D>,
>(config: C): Promise<AxiosResponse<T, D>> {
	const restConfig = {...(config as AxiosRequestConfig<D>)};
	delete restConfig.baseURL;
	return instance.request<T, AxiosResponse<T, D>, D>({
		...restConfig,
		withCredentials: true, // enforce even if caller overrides
	});
}

export type CustomInstance = typeof customInstance;

/**
 * If you need the underlying Axios instance (e.g., for adding interceptors
 * dynamically elsewhere in the app) you can export it. Keep internal by
 * default to reduce public API surface.
 */
export {instance as __axiosInstance}; // (Optional) remove if not needed

/**
 * Allows consumers to set the API base URL at runtime.
 */
export function setApiBaseUrl(url: string) {
	instance.defaults.baseURL = url;
}

export function getApiBaseUrl() {
	return instance.defaults.baseURL;
}

// Allow for dynamic token source (e.g., from React context or props)
export function setMyYoastAccessTokenFactory(getToken: () => string) {
	myYoastAccessTokenFactory = getToken;
}

export function getMyYoastAccessTokenFactory() {
	return myYoastAccessTokenFactory;
}

let loginPromise: Promise<string> | null = null;

async function login(): Promise<string> {
	const myYoastToken = myYoastAccessTokenFactory();
	const baseURL = instance.defaults.baseURL;
	const sessionKey = getCurrentSessionTokenKey(myYoastToken, baseURL);
	const yaibiToken = sessionStorage.getItem(sessionKey);
	if (yaibiToken) {
		try {
			const token = JSON.parse(yaibiToken);
			if (token?.validUntil > Date.now() && token.value) {
				return String(token.value);
			}
		} catch {
			// ignore parsing errors and fall through to re-login
		}
	}

	// If a login is already in progress, return the same promise
	if (loginPromise) {
		return loginPromise;
	}

	loginPromise = (async () => {
		try {
			const xsrfToken = await getXSRFToken();
			const response = await axios.post(
				baseURL + '/users/getToken',
				{},
				{
					headers: {
						'Authorization': 'Bearer ' + myYoastToken,
						...(xsrfToken ? {'X-XSRF-TOKEN': xsrfToken} : {}),
					},
					withCredentials: true,
				}
			);
			const responseBody: GetToken200 & unknown = response.data;
			if (responseBody?.plainTextToken) {
				sessionStorage.setItem(
					sessionKey,
					JSON.stringify({
						value: responseBody.plainTextToken,
						validUntil: Date.parse(responseBody.accessToken?.expires_at || '') || (Date.now() + 15 * 60 * 1000), // fallback to 15 min if missing
					})
				);
				return responseBody.plainTextToken;
			}
		} catch (error) {
			if (axios.isAxiosError(error)) {
				if (error.response) {
					console.error('Login failed with status', error.response.status, error.response.data);
				} else if (error.request) {
					console.error('Login request made but no response received', error.request);
				} else {
					console.error('Error setting up login request', error.message);
				}
			} else {
				console.error('Non-Axios error during login', error);
			}
		} finally {
			// Clear the promise so future requests can retry
			loginPromise = null;
		}
		return '';
	})();

	return loginPromise;
}


function getCurrentSessionTokenKey(myYoastToken: string, baseURL?: string): string {
	// turn the key into a short hash. Don't need to be cryptographically secure, just unique-ish.
	const hash = Array.from(myYoastToken + baseURL).reduce((hashAcc, char) => {
		return ((hashAcc << 5) - hashAcc) + char.charCodeAt(0);
	}, 0);

	return LOCAL_STORAGE_AUTH_KEY + ":" + hash;
}

let XSRFPromise: Promise<string> | null = null;
async function getXSRFToken(){
	// Only attempt to fetch CSRF cookie if on the same domain as the API.
	if (window.location.hostname !== new URL(getApiBaseUrl() || window.location.href).hostname) {
		// No need to fetch CSRF cookie if on a different domain than the API.
		return '';
	}

	// This sets the XSRF-TOKEN cookie from the backend (Laravel Sanctum endpoint)
	// which is then automatically added to subsequent requests by axios.
	// We do this on-demand to avoid unnecessary calls if the cookie is already present.
	const cookieValue = getCookie('XSRF-TOKEN');
	if (cookieValue) {
		return decodeURIComponent(cookieValue);
	}
	if(XSRFPromise){
		return XSRFPromise;
	}
	XSRFPromise = (async () => {
		try {
			await axios.get(getApiBaseUrl()?.replace("/api/v1",'')+`/sanctum/csrf-cookie`, {withCredentials: true,});
			return decodeURIComponent(getCookie('XSRF-TOKEN') || '');
		} finally {
			XSRFPromise = null;
		}
	})();
	return XSRFPromise;
}
